import { createSubjectAttributes, ExperimentationProvider, Platform, ProjectName } from '@user-portal/experimentation';
import { FC, ReactNode, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { getLanguage } from 'api/utils/languageManagment';

import { selectCurrentUser } from 'store/user/selectors';

import { PLATFORM_BY_KEY } from 'constants/api';
import { SOURCE } from 'constants/general';

import { getAgeFromBirthday, toYYMMDD } from 'helpers/date';
import { getDeviceType, getEmailDomain } from 'helpers/utils';

import { experimentationConfig } from './experimentationConfig';

interface ExperimentationWrapperProps {
    children: ReactNode;
}

export const ExperimentationWrapper: FC<ExperimentationWrapperProps> = ({ children }) => {
    const user = useSelector(selectCurrentUser);

    const subjectAttributes = useMemo(() => {
        const userId = user.user_id ? String(user.user_id) : 'anonymous';

        return createSubjectAttributes(userId, userId, {
            // Application info
            platform: Platform.WEB,
            source_platform: Platform.WEB,
            source_project: ProjectName.YOGA,

            // User properties
            age: getAgeFromBirthday(user.date_of_birth),
            gender: user.gender || undefined,
            ...(user.created_at && { created_at: user.created_at }),
            ...(user.created_at && { created_at_date: toYYMMDD(user.created_at) }),

            ...(user.first_login_at && { first_login_at: user.first_login_at }),
            ...(user.first_login_at && { first_login_at_date: toYYMMDD(user.first_login_at) }),

            is_active_plan: user.is_active_plan || undefined,

            // Fitness specific
            problem_zones: user.problem_zones || undefined,
            fitness_level: user.fitness_level || undefined,
            weight: user.weight || undefined,
            height: user.height || undefined,
            scale: user.units || undefined,
            goal: user.goal || undefined,
            target_weight: user.target_weight || undefined,

            // Other attributes
            ...(user.nps_group && { nps_group: user.nps_group }),
            ...(user.payment_platform && {
                payment_platform: PLATFORM_BY_KEY[user.payment_platform]?.toUpperCase(),
            }),
            reteno_contact_id: user.reteno_contact_id ? String(user.reteno_contact_id) : undefined,
            country: user.country || undefined,

            w2a_branch: user.w2a_branch || undefined,

            extras: {
                device_type: getDeviceType(),
                language: getLanguage(),
                email_domain: getEmailDomain(user.email) || '',
                source: SOURCE,
            },
        });
    }, [user]);

    return (
        <ExperimentationProvider config={experimentationConfig} subjectAttributes={subjectAttributes}>
            {children}
        </ExperimentationProvider>
    );
};
